import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { fetchWithToken } from '../../services/fetch-with-token';
import {
  AlertService,
  Button,
  Column,
  DataTableNaked,
  Modal,
  ModalContent,
  ModalFooter,
  ReturnIcon,
  RowAction,
  StatusBadge,
  TextInput,
  Theme,
} from '@spoiler-alert/ui-library';
import AppSettings from '../../app-settings';

const useStyles = createUseStyles({
  actions: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  tableWrap: {
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  modalContent: {
    width: 480,
  },
  field: {
    marginBottom: 16,
  },
});

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const retentionDays = (createdAt, expiresAt) => {
  const days = Math.round((new Date(expiresAt) - new Date(createdAt)) / (1000 * 60 * 60 * 24));
  return `${days} days`;
};

const defaultForm = { description: '', daysToRetain: '' };

export default function DataRestoreWidget() {
  const classes = useStyles();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const getRestoreList = async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(
        `${AppSettings.GRAPHQL_SERVER_URL.replace('admin/', '')}backup-restore/restore/list`,
        { method: 'GET' },
      );
      if (!response.ok) {
        const errorText = await response.text().catch(() => null);
        throw new Error(errorText || `Failed to fetch restore list (${response.status})`);
      }
      const data = await response.json();
      const results = data.results ?? data;
      setSnapshots(
        results.map((s) => ({
          ...s,
          _id: s.id,
          retention: retentionDays(s.createdAt, s.expiresAt),
          createdAtFormatted: formatDate(s.createdAt),
          expiresAtFormatted: formatDate(s.expiresAt),
        })),
      );
    } catch (err) {
      AlertService.alert({ type: 'warning', message: err.message || 'Failed to fetch restore list.' });
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setForm(defaultForm);
    setModalOpen(true);
  };

  const createSnapshot = async () => {
    setCreating(true);
    try {
      const response = await fetchWithToken(
        `${AppSettings.GRAPHQL_SERVER_URL.replace('admin/', '')}backup-restore/restore/create-snapshot`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description: form.description, retentionInDays: Number(form.daysToRetain) }),
        },
      );
      if (!response.ok) {
        const errorText = await response.text().catch(() => null);
        throw new Error(errorText || `Failed to create snapshot (${response.status})`);
      }
      setModalOpen(false);
    } catch (err) {
      AlertService.alert({ type: 'warning', message: err.message || 'Failed to create snapshot.' });
    } finally {
      setCreating(false);
    }
  };

  const restoreSnapshot = async () => {
    setRestoring(true);
    try {
      const response = await fetchWithToken(
        `${AppSettings.GRAPHQL_SERVER_URL.replace('admin/', '')}backup-restore/restore/snapshot`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ snapshotId: restoreTarget.id }),
        },
      );
      if (!response.ok) {
        const errorText = await response.text().catch(() => null);
        throw new Error(errorText || `Failed to restore snapshot (${response.status})`);
      }
      setRestoreTarget(null);
    } catch (err) {
      AlertService.alert({ type: 'warning', message: err.message || 'Failed to restore snapshot.' });
    } finally {
      setRestoring(false);
    }
  };

  const columns = useMemo(
    () => [
      new Column({
        field: 'description',
        displayName: 'Description',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'asc',
        formatter: (v) => v || 'Automated snapshot',
      }),
      new Column({ field: 'frequencyType', displayName: 'Scheduling Type', sortable: true }),
      new Column({ field: 'retention', displayName: 'Retention', sortable: false }),
      new Column({ field: 'createdAtFormatted', displayName: 'Created', sortable: true }),
      new Column({ field: 'expiresAtFormatted', displayName: 'Expires', sortable: true }),
      new Column({
        field: 'status',
        displayName: 'Status',
        sortable: true,
        formatter: (v) => <StatusBadge status={v} />,
      }),
    ],
    [],
  );

  return (
    <div>
      <h4>Restore Data</h4>
      <div className={classes.actions}>
        <Button onClick={getRestoreList} disabled={loading}>
          Get Restore List
        </Button>
        <Button onClick={openModal}>Create Snapshot</Button>
      </div>
      {(loading || snapshots.length > 0) && (
        <div className={classes.tableWrap}>
          <DataTableNaked
            data={snapshots}
            columns={columns}
            loading={loading}
            rowActions={[
              <RowAction
                key="restore"
                tooltipText="Restore"
                icon={ReturnIcon}
                onClick={(row) => () => setRestoreTarget(row)}
                secondary
              />,
            ]}
          />
        </div>
      )}
      <Modal open={modalOpen} onHide={() => setModalOpen(false)} closeOnEsc closeOnOutsideClick>
        <ModalContent>
          <div className={classes.modalContent}>
            <h2>Create Snapshot</h2>
            <div className={classes.field}>
              <TextInput
                labelText="Description"
                type="text"
                value={form.description}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              />
            </div>
            <div className={classes.field}>
              <TextInput
                labelText="Days to Retain"
                type="number"
                value={form.daysToRetain}
                onChange={(v) => setForm((f) => ({ ...f, daysToRetain: v }))}
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" link onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            primary
            disabled={creating || !form.description || !form.daysToRetain}
            loading={creating}
            loadingText="Creating..."
            onClick={createSnapshot}
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={!!restoreTarget} onHide={() => setRestoreTarget(null)} closeOnEsc closeOnOutsideClick>
        <ModalContent>
          <div className={classes.modalContent}>
            <h2>Restore Snapshot</h2>
            <p>
              This will cause the current environment to be unavailable for around 10 minutes and will restore data to
              the selected snapshot. Are you sure you want to restore to this point in time?
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" link onClick={() => setRestoreTarget(null)}>
            Cancel
          </Button>
          <Button type="submit" warning loading={restoring} loadingText="Restoring..." onClick={restoreSnapshot}>
            Restore
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
